import { mapKeys, omit, pick } from 'radash';

export const AB_TEST_TAB_PREFIX = 'ab_test_' as const;

const overwriteWithAbTestOnValues = <
  TDocument extends Record<string, unknown>,
  TPrefix extends string,
>(
  document: TDocument,
  abTestTabPrefix: TPrefix,
) => {
  const abTestPrefixedKeys = Object.keys(document).filter((key) =>
    key.startsWith(abTestTabPrefix),
  );

  const abTestOnValues = pick(document, abTestPrefixedKeys);
  const abTestOffValues = omit(document, abTestPrefixedKeys);

  const switchedAbTestObject = mapKeys(abTestOnValues, (key) =>
    key.replace(abTestTabPrefix, ''),
  );

  return { ...abTestOffValues, ...switchedAbTestObject };
};

const removeAbTestKeys = <
  TDocument extends Record<string, unknown>,
  TPrefix extends string,
>(
  document: TDocument,
  abTestTabPrefix: TPrefix,
) => {
  const filteredKeys = Object.keys(document).filter((key) =>
    key.startsWith(abTestTabPrefix),
  );

  return omit(document, filteredKeys);
};

/**
 * Helper function that handle prismic document's 'AB Test' tabs.
 * - If `isAbTestEnabled` is false, the returned doc contains just the content of 'Main' tab (non-prefixed keys)
 * - If `isAbTestEnabled` is true, the returned doc contains the content of 'Main tab' (non-prefixed keys) with values overwritten with the 'AB Test' tab content (prefixed-keys)
 *
 * @example
 * const inputDoc = { title: 'title A', ab_test_title: 'title B', description: 'description A' };
 * const outputDoc = computeDocumentsContentToDisplay({ document: inputDoc, abTestTabPrefix: 'ab_test_', isAbTestEnabled: true });
 * // -> { title: 'title B', description: 'description A' }
 *
 * const inputDoc = { title: 'title A', ab_test_title: 'title B', description: 'description A' };
 * const outputDoc = computeDocumentsContentToDisplay({ document: inputDoc, abTestTabPrefix: 'ab_test_', isAbTestEnabled: false });
 * // -> { title: 'title A', description: 'description A' }
 */
export const computeDocumentsContentToDisplay = <
  TDocument extends Record<string, unknown>,
  TPrefix extends string,
>({
  document,
  abTestTabPrefix,
  isAbTestEnabled,
}: {
  document: TDocument | null;
  abTestTabPrefix: TPrefix;
  isAbTestEnabled: boolean;
}): Omit<TDocument, `${TPrefix}${string}`> | null => {
  if (!document) {
    return null;
  }

  if (isAbTestEnabled) {
    // we make the 'ab_test_' keys be the non-prefixed keys
    return overwriteWithAbTestOnValues(document, abTestTabPrefix) as Omit<
      TDocument,
      `${TPrefix}${string}`
    >;
  }

  // we remove the 'ab_test_' prefixed keys
  return removeAbTestKeys(document, abTestTabPrefix) as Omit<
    TDocument,
    `${TPrefix}${string}`
  >;
};
